import type { FC } from 'react';

import { useCms, useContent, useImageMapper, useSetting } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import type { WieDoetErMeeContentCardContent } from '@eneco/cms-types/generated/WieDoetErMeeContentCard';
import RichText from '@eneco/components/RichText/RichText';
import {
  CampaignCard,
  type CampaignCardBackground,
  type CampaignCardObjectFit,
  toPromotionalCardTitle,
} from '@eneco/custom-components/CampaignCard';
import { ButtonLink, Stack, Stretch, Text, TextLink } from '@eneco/sparky';
import type { HeadingProps } from '@eneco/sparky/types';

/**
 * `WieDoetErMeeContentCard` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors the legacy Sitecore lane (`libs/sitecore/components/content/src/CampaignContentCard/
 * CampaignContentCard.tsx`, registered there under this name and as `CampaignContentCard`). The card
 * itself is `@eneco/custom-components/CampaignCard`, which the legacy stylesheet moved into verbatim
 * and which the legacy component renders too. This file supplies only the content and the CTAs.
 *
 * The CTAs mirror the legacy `FlatContentCardButtonLink`/`FlatContentCardTextLink` markup with a plain
 * Sparky link on the converted `Link`, not `@eneco/link`'s `useLinkComponent` — the same substitution
 * every other block in this lane makes. The body goes through `adapter.toRichText`, so Sanity's
 * Portable Text renders as well as Sitecore's HTML.
 *
 * Drops the Sitecore Experience Editor branch — this lane never runs inside the Experience Editor.
 */
const WieDoetErMeeContentCard: FC = () => {
  const { adapter } = useCms();
  const { firstTitle, secondTitle, thirdTitle, fourthTitle, content, image, buttonLink, link } =
    useContent<WieDoetErMeeContentCardContent>();
  const mapImage = useImageMapper();
  const headingLevel = useSetting('headingLevel');
  const background = useSetting('cardBackgroundOption');
  const objectFit = useSetting('objectFit');
  // Sitecore sends elevation as "1" / "0", Sanity as a boolean.
  const elevation = useSetting('setElevationOption');

  const richText = adapter.toRichText(content);
  const buttonCta = linkWithUrl(buttonLink);
  const textCta = linkWithUrl(link);

  return (
    <CampaignCard
      promotionalTitle={toPromotionalCardTitle([firstTitle, secondTitle, thirdTitle, fourthTitle])}
      image={mapImage(image)}
      headingLevel={(headingLevel || 'h3') as HeadingProps['as']}
      background={(background ?? 'white') as CampaignCardBackground}
      objectFit={(objectFit ?? 'cover') as CampaignCardObjectFit}
      isElevated={elevation === '1' || elevation === 'true'}>
      {richText ? (
        <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
          <RichText html={richText} />
        </Text>
      ) : null}
      {buttonCta?.text ? (
        <Stack.Item>
          <Stretch width={{ initial: true, md: false }}>
            <ButtonLink href={buttonCta.href} target={buttonCta.target}>
              {buttonCta.text}
            </ButtonLink>
          </Stretch>
        </Stack.Item>
      ) : null}
      {textCta?.text ? (
        <Stack.Item>
          <TextLink emphasis="high" href={textCta.href} target={textCta.target}>
            {textCta.text}
          </TextLink>
        </Stack.Item>
      ) : null}
    </CampaignCard>
  );
};

export default WieDoetErMeeContentCard;
